import { useState, useEffect, useMemo } from 'react';
import { FinancingObligationsChart } from './financingObligations.jsx'; 
import { TimelineComponent } from './chronoTimeline.jsx'; 
import { DiffusionObligationsComponent } from './diffusionObligations.jsx'
import { RegulationTextsComponent } from './regulationTexts.jsx';
import './broadcasterModule.css';

const YearSelector = ({ selectedYear, onYearChange, minYear, maxYear }) => {
    return (
        <div className="year-selector-container">
            {/* <button 
                className="year-arrow" 
                onClick={() => onYearChange(selectedYear - 1)}
                disabled={selectedYear <= minYear}
                title={selectedYear <= minYear ? "Aucune donnée antérieure" : "Année précédente"}
            >
                &#10094;
            </button> */}

            <div className="year-carousel">
                <span 
                    className="year-side" 
                    onClick={() => selectedYear > minYear && onYearChange(selectedYear - 1)}
                    style={{ visibility: selectedYear > minYear ? 'visible' : 'hidden' }}
                >
                    {selectedYear - 1}
                </span>
                
                <span className="year-center active">
                    {selectedYear}
                </span>
                
                <span 
                    className="year-side" 
                    onClick={() => selectedYear < maxYear && onYearChange(selectedYear + 1)}
                    style={{ visibility: selectedYear < maxYear ? 'visible' : 'hidden' }}
                >
                    {selectedYear + 1}
                </span>
            </div>

            {/* <button 
                className="year-arrow" 
                onClick={() => onYearChange(selectedYear + 1)}
                disabled={selectedYear >= maxYear}
                title={selectedYear >= maxYear ? "Aucune donnée ultérieure" : "Année suivante"}
            >
                &#10095;
            </button> */}
        </div>
    );
};

export const BroadcasterModule = ({ 
  allRulesData, 
  broadcasters, 
  zoomScale,
  initialBroadcasterId,
  onBroadcasterChange, // NEW
  onRuleClick,
  selectedRuleId,
  parentRuleId,
  isPickingParent
}) => {
  const [filteredData, setFilteredData] = useState(null);
  const [selectedBroadcaster, setSelectedBroadcaster] = useState(initialBroadcasterId || ''); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (initialBroadcasterId) {
        setSelectedBroadcaster(initialBroadcasterId);
    }
  }, [initialBroadcasterId]);

  useEffect(() => {
    if (selectedBroadcaster) {
      const newData = allRulesData.find(item => item.broadcaster_id === selectedBroadcaster);
      setFilteredData(newData);
    } else {
      setFilteredData(null);
    }
  }, [allRulesData, selectedBroadcaster]);

  const handleBroadcasterChange = (event) => {
    const newId = event.target.value;
    setSelectedBroadcaster(newId);
    // NEW: Notify parent if callback exists
    if (onBroadcasterChange) {
        onBroadcasterChange(newId);
    }
  };

  const handleMouseDownOnSelect = (event) => {
    event.stopPropagation(); 
  };

  const yearBounds = useMemo(() => {
      let min = new Date().getFullYear();
      let max = new Date().getFullYear();
      let hasNullEndYear = false;

      if (!filteredData || !filteredData.rules) return { min, max };

      const checkRule = (rule) => {
          if (rule && rule.application_periods) {
              rule.application_periods.forEach(p => {
                  if (p.start_year && p.start_year < min) min = p.start_year;
                  if (p.end_year) {
                      if (p.end_year > max) max = p.end_year;
                  } else {
                      hasNullEndYear = true; // Règle "En cours" (sans date de fin)
                  }
              });
          }
      };

      (filteredData.rules.financingObligations || []).forEach(checkRule);
      (filteredData.rules.broadcastObligations || []).forEach(checkRule);
      (filteredData.rules.chronoWindows || []).forEach(checkRule);

      // Si des données sont "en cours", on permet l'année prochaine pour faire des projections
      if (hasNullEndYear) {
          max = Math.max(max, new Date().getFullYear() + 1);
      }

      return { min, max };
  }, [filteredData]);

  return (
    <div className="broadcaster-module">
      <div className="broadcaster-header">
        
        <select
          id={`broadcaster-select-${Math.random().toString(36).substr(2, 9)}`} 
          className="broadcaster-select"
          value={selectedBroadcaster}
          onChange={handleBroadcasterChange}
          onMouseDown={handleMouseDownOnSelect}
          style={{ margin: 0 }}
        >
          <option key="placeholder" value="" disabled>Diffuseur</option>
          {broadcasters.map(broadcaster => (
            <option key={broadcaster.broadcaster_id} value={broadcaster.broadcaster_id}>
              {broadcaster.broadcaster_name}
            </option>
          ))}
        </select>

        {selectedBroadcaster !== '' && filteredData && (
          <>
            <YearSelector 
                selectedYear={selectedYear} 
                onYearChange={setSelectedYear} 
                minYear={yearBounds.min}
                maxYear={yearBounds.max}
            />
            <div className="broadcaster-meta" style={{ display: 'flex', gap: '15px', alignItems: 'center', margin: 0 }}>
              <p className="broadcaster-type" style={{ margin: 0 }}>&bull; {filteredData.broadcaster_type}</p>
              <p className="broadcaster-hq" style={{ margin: 0 }}>&bull; {filteredData.broadcaster_hq_abroad === true ? "Siège à l'étranger" : "Siège en France"}</p>
            </div>
          </>
        )}
      </div>

      {selectedBroadcaster !== '' && filteredData && (
        <>
          <div className="broadcaster-grid-container">
            {(filteredData.broadcaster_comment !== null && filteredData.broadcaster_comment !== '') && <div className="broadcaster-comment module-section">{filteredData.broadcaster_comment}</div>}
            <div className="broadcaster-grid">
              <FinancingObligationsChart 
                selectedYear={selectedYear}
                data={filteredData.rules.financingObligations.sort(({ id: a }, { id: b }) => a - b)} 
                externalZoomScale={zoomScale} 
                onRuleClick={onRuleClick}
                selectedRuleId={selectedRuleId}
                parentRuleId={parentRuleId}
                isPickingParent={isPickingParent}
              />
              <TimelineComponent 
                selectedYear={selectedYear}
                data={filteredData.rules.chronoWindows} 
                onRuleClick={onRuleClick}
                selectedRuleId={selectedRuleId} 
              />
              <DiffusionObligationsComponent 
                selectedYear={selectedYear}
                data={filteredData.rules.broadcastObligations} 
                onRuleClick={onRuleClick}
                selectedRuleId={selectedRuleId} 
              />
              <RegulationTextsComponent data={filteredData.regulation_texts} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};